import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import GlobalError from '../components/GlobalError';
import ProductList from '../components/ProductList';
import Pagination from '../components/Pagination';
import Breadcrumbs from '../components/Breadcrumbs';
import { Typography, Chip, Stack } from '@mui/material';

const CategoryPage = () => {
  const { category_id } = useParams();
  const navigate = useNavigate();

  const [newProducts, setNewProducts] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [categoryData, setCategoryData] = useState(null);

  const [dataset, setDataset] = useState([]);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchProducts = async (page = 1) => {

    try {

      const response = await axiosInstance.get(
        `/category/${category_id}/products/user`,
        {
          params: { page },
          withCredentials: true
        }
      );

      setDataset(response.data.data || []);
      setCurrentPage(response.data.currentPage || page);
      setTotalPages(response.data.totalPages || 1);

    } catch (error) {

      if (error.response?.status === 401) {

        try {

          const publicResponse = await axiosInstance.get(
            `/category/${category_id}/products`,
            {
              params: { page },
              withCredentials: false
            }
          );

          setDataset(publicResponse.data.data || []);
          setCurrentPage(publicResponse.data.currentPage || page);
          setTotalPages(publicResponse.data.totalPages || 1);

        } catch (err) {

          setError(
            err.response?.data?.message ||
            err.message ||
            '商品一覧の取得に失敗しました'
          );

          setDataset([]);
        }

      } else {

        setError(
          error.response?.data?.message ||
          error.message ||
          '商品一覧の取得に失敗しました'
        );

        setDataset([]);
      }
    }
  };

  useEffect(() => {

    const fetchCategoryData = async () => {

      try {

        setError(null);

        const categoryResponse = await axiosInstance.get(
          `/category/${category_id}`,
          {
            withCredentials: false
          }
        );

        setCategoryData(categoryResponse.data);

      } catch (err) {

        setError(
          err.response?.data?.message ||
          err.message ||
          'カテゴリー情報の取得に失敗しました'
        );
      }
    };

    fetchCategoryData();

  }, [category_id]);

  useEffect(() => {

    const fetchSideProducts = async () => {

      try {

        let newProductsResponse;
        let relatedProductsResponse;

        try {

          [
            newProductsResponse,
            relatedProductsResponse
          ] = await Promise.all([
            axiosInstance.get('/product/new/user', {
              withCredentials: true
            }),
            axiosInstance.get(`/product/related/${category_id}/user`, {
              withCredentials: true
            }),
          ]);

        } catch (error) {

          if (error.response?.status === 401) {

            [
              newProductsResponse,
              relatedProductsResponse
            ] = await Promise.all([
              axiosInstance.get('/product/new', {
                withCredentials: false
              }),
              axiosInstance.get(`/product/related/${category_id}`, {
                withCredentials: false
              }),
            ]);

          } else {
            throw error;
          }
        }

        setNewProducts(newProductsResponse.data || []);
        setRelatedProducts(relatedProductsResponse.data || []);

      } catch (err) {

        console.error(err);
      }
    };

    fetchSideProducts();

  }, [category_id]);

  useEffect(() => {

    fetchProducts(1);

  }, [category_id]);

  const handlePageChange = async (page) => {

    await fetchProducts(page);

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (error) {
    return <GlobalError errorMessage={error} />;
  }

  if (!categoryData) {
    return null;
  }

  return (
    <>
      {category_id && (
        <Breadcrumbs
          categoryId={category_id}
          className="breadcrumb"
        />
      )}

      <Typography variant="h1">
        {categoryData.category.category_name}
      </Typography>

      {categoryData.sub_categories?.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
        >
          {categoryData.sub_categories.map((subCategory) => (
            <Chip
              key={subCategory.category_id}
              label={subCategory.category_name}
              clickable
              onClick={() => {
                navigate(`/category/${subCategory.category_id}`);
              }}
            />
          ))}
        </Stack>
      )}

      {dataset.length > 0 ? (
        <ProductList products={dataset} />
      ) : (
        <Typography sx={{ py: 4 }}>
          現在、表示できる商品がありません
        </Typography>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {relatedProducts?.length > 0 && (
        <>
          <Typography variant="h2" mt={8}>
            関連商品
          </Typography>

          <ProductList products={relatedProducts} />
        </>
      )}

      {newProducts?.length > 0 && (
        <>
          <Typography variant="h2" mt={8}>
            新商品
          </Typography>

          <ProductList products={newProducts} />
        </>
      )}
    </>
  );
};

export default CategoryPage;